var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FEEDBACK_REASON_CODE, feedbackVersionNumber, initialMessages, negativeFeedbackTemplate, WIDGET_ID, positiveFeedbackTemplate, } from '../../config/constants';
import { useCallback, useEffect, useState } from 'react';
import ChatBotContent from './components/chatBotContent';
import ChatBotFooter from './components/chatBotFooter';
import ChatBotHeader from './components/chatBotHeader';
import HighlightExplain from './components/highlightExplain';
import styles from './styles/chatbot.module.scss';
import { generateWebSocketUrlFromInitData, getRestURL } from '../../utils/getUrls';
import { ClientProvider, useClient } from '../../client/ClientProvider';
import { AuthCookieProvider } from "./components/AuthCookieProvider";
import useWebSocket from "react-use-websocket";
import { ErrorBoundary } from "react-error-boundary";
const ChatBotWidget = (initData) => {
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [userInput, serUserInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(true);
    const [isHistory, setIsHistory] = useState(false);
    const client = useClient();
    const [sessionId, setSessionId] = useState('');
    const [isMaximized, setIsMaximized] = useState(false);
    const [isInitSessionCompleted, setIsInitSessionCompleted] = useState(false);
    const toggleMaximized = () => {
        setIsMaximized((prevState) => !prevState);
    };
    const { sendMessage, lastMessage } = useWebSocket(generateWebSocketUrlFromInitData(initData), {
        shouldReconnect: (_) => true,
    });
    const handleInputChange = (userInput) => {
        serUserInput(userInput);
    };
    const getInitSession = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const initSessionData = yield client.fetchInitSessionInfo(initData.blueprintArn);
            console.log(`Received InitSessionInfo: ${JSON.stringify(initSessionData)}`);
            setIsChatHistoryLoading(false);
            setSessionId(initSessionData.sessionId);
            setIsInitSessionCompleted(true);
            if (initSessionData.zeroStateExperienceFlag) {
                setMessages(initialMessages);
            }
            else {
                setIsHistory(true);
                const chatHistory = [];
                for (const chatMessage of initSessionData.chatHistory) {
                    const message = {
                        messageId: chatMessage.messageId,
                        text: chatMessage.messageText,
                        sender: chatMessage.messageOwner === 'user' ? 'user' : 'bot',
                        type: chatMessage.messageOwner === 'user' ? 'request' : 'response',
                        feedback: chatMessage.feedback,
                        feedbackReasonCode: chatMessage.feedbackReasonCode,
                    };
                    chatHistory.push(message);
                    if (message.feedbackReasonCode) {
                        chatHistory.push({
                            messageId: chatMessage.messageId,
                            text: negativeFeedbackTemplate[feedbackVersionNumber].text,
                            sender: 'bot',
                            type: 'options',
                            options: negativeFeedbackTemplate[feedbackVersionNumber].options,
                            selection: message.feedbackReasonCode,
                        });
                    }
                }
                setMessages(chatHistory);
            }
        }
        catch (error) {
            setIsChatHistoryLoading(false);
            setIsInitSessionCompleted(true);
            console.error(error);
        }
    }), [client, initData.blueprintArn]);
    const sendUserMessage = useCallback((userMessage) => {
        setIsHistory(false);
        setMessages((prevMessage) => [...prevMessage, userMessage]);
        serUserInput('');
        const message = JSON.stringify(Object.assign({ sessionId: sessionId, action: 'sendMessage', message: userMessage.text, response_format: 'html' }, (initData.blueprintArn && { blueprintArn: initData.blueprintArn })));
        console.log(`Send message: ${message}`);
        sendMessage(message);
        setIsLoading(true);
    }, [initData.blueprintArn, sendMessage, sessionId]);
    const handleMessage = useCallback((rawData) => {
        const data = JSON.parse(rawData);
        console.log(`WebSocket message received: ${rawData}`); // Let's keep this here for now while developing
        if (data.message !== '') {
            const botMessage = {
                text: data.message,
                sender: 'bot',
                type: 'response',
                messageId: data.message_id
            };
            setMessages((prevMessage) => [...prevMessage, botMessage]);
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (lastMessage !== null) {
            handleMessage(lastMessage.data);
        }
    }, [handleMessage, lastMessage]);
    const handleSendMessage = () => {
        if (userInput.trim() !== '') {
            const userMessage = { text: userInput, sender: 'user' };
            sendUserMessage(userMessage);
        }
    };
    /**
     * @param {string} sessionId Need @jie's input on this
     * @param {string} messageId Need @jie's input on this
     * @param {string} feedback Feedback sends to backend
     * @param {string} feedbackReasonCode Feedback reason send to backend, backend seems doesn't have a fixed code for it for now.
     * @todo Please follow up on the token and sessionId, messageId
     */
    const submitFeedback = (messageId_1, feedback_1, ...args_1) => __awaiter(void 0, [messageId_1, feedback_1, ...args_1], void 0, function* (messageId, feedback, feedbackReasonCode = '') {
        const feedbackMessage = {
            sessionId: sessionId,
            messageId: messageId,
            feedback: feedback,
            feedbackReasonCode: feedbackReasonCode,
        };
        console.log(`Submit feedback: ${JSON.stringify(feedbackMessage)}`);
        yield client.submitFeedback(feedbackMessage);
    });
    const handleOptionButtonClick = (msg, option) => {
        if (msg.selection)
            return;
        const optionCode = FEEDBACK_REASON_CODE[option];
        if (negativeFeedbackTemplate[feedbackVersionNumber].options.indexOf(option.trim()) >= 0) {
            msg.selection = FEEDBACK_REASON_CODE[option];
            const botMessage = { text: negativeFeedbackTemplate[feedbackVersionNumber].response, sender: 'bot' };
            setMessages((prevMessage) => [...prevMessage, botMessage]);
            if (msg.messageId) {
                submitFeedback(msg.messageId, 'Negative', optionCode);
            }
        }
        else if (option.trim() !== '') {
            const userMessage = { text: option, sender: 'user' };
            sendUserMessage(userMessage);
        }
    };
    const explainText = useCallback((text) => {
        if (text.trim() !== '') {
            const userMessage = {
                text: `I have a question about ${text}`,
                sender: 'user'
            };
            sendUserMessage(userMessage);
        }
    }, [sendUserMessage]);
    const expandIfClosed = useCallback(() => {
        if (!expanded)
            setExpanded(true);
    }, [expanded]);
    useEffect(() => {
        getInitSession().catch(err => console.log(err));
        // Want to execute this block only in initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        document.addEventListener('openWidget', expandIfClosed);
        return () => {
            document.removeEventListener('openWidget', expandIfClosed);
        };
    }, [expandIfClosed]);
    const openChatBot = () => setExpanded(true);
    const closeChatBot = () => setExpanded(false);
    const handleFeedbackButtonClick = (msg, feedback) => {
        msg.feedback = feedback;
        if (feedback === 'Negative') {
            setMessages((prevMessage) => [...prevMessage, {
                    text: negativeFeedbackTemplate[feedbackVersionNumber].text,
                    sender: 'bot',
                    type: 'options',
                    options: negativeFeedbackTemplate[feedbackVersionNumber].options,
                    messageId: msg.messageId
                }]);
        }
        else if (feedback === 'Positive') {
            setMessages((prevMessage) => [...prevMessage, {
                    text: positiveFeedbackTemplate[feedbackVersionNumber].text,
                    sender: 'bot',
                    type: 'response',
                    feedback: 'disabled',
                    messageId: msg.messageId
                }]);
            if (msg.messageId) {
                submitFeedback(msg.messageId, 'Positive');
            }
        }
    };
    return (_jsxs("div", { className: `${styles.chatbot} ${styles['chatbot-button']} ${expanded ? `${styles.expanded} ${isMaximized ? styles['maximized-width'] : ''}` : styles['chatbot-button']}`, id: WIDGET_ID, children: [_jsx(ChatBotHeader, { expanded: expanded, isLoading: isChatHistoryLoading, openChatBot: openChatBot, closeChatBot: closeChatBot, isMaximized: isMaximized, toggleMaximized: toggleMaximized }), expanded && (_jsxs(_Fragment, { children: [_jsx(ChatBotContent, { messages: messages, handleOptionButtonClick: handleOptionButtonClick, handleFeedbackButtonClick: handleFeedbackButtonClick, showLoadingDots: isLoading, displayBottom: isHistory }), _jsx(ChatBotFooter, { userInput: userInput, onInputChange: handleInputChange, onEnterPress: handleSendMessage, isMaximized: isMaximized, isLoading: isLoading, isInitSessionCompleted: isInitSessionCompleted })] })), _jsx(HighlightExplain, { onExplain: explainText, expandIfClosed: expandIfClosed })] }));
};
const ChatBot = (initData) => {
    if (!initData.gandalfToken && !initData.exemptGandalfToken) {
        console.warn('No token is passed from parent.');
        return _jsx(_Fragment, {});
    }
    return (_jsx(ErrorBoundary, { fallback: _jsx(_Fragment, {}), children: _jsx(ClientProvider, { endpoint: getRestURL(window.location.hostname), initData: initData, children: _jsx(AuthCookieProvider, { gandalfToken: initData.gandalfToken, children: _jsx(ChatBotWidget, Object.assign({}, initData)) }) }) }));
};
export default ChatBot;
